/**
 * OcrolusBookCard — the status rail beside the Ocrolus work surface.
 *
 * It answers one question at a glance: what does Ocrolus currently say about
 * this loan's money? So the verified balance is the hero, the two numbers an
 * underwriter reaches for next (net cash flow, what was analyzed) sit under it,
 * fraud signals get their own band, and the plumbing — book name, uuid,
 * pipeline, note version — folds away behind "Book details", because it's
 * reference material rather than a finding.
 *
 * Everything here comes from GET /files/ocrolus_book/{dealId}: `filesSent`,
 * `accountsReady` / `cashFlowReady`, `signals`, `ocrolusNote`, and the
 * `bankStatementSummary` the loan carries (accounts[], cash_flow,
 * total_latest_balance — shape in backfill_ocrolus_summaries.py).
 *
 * The card sticks to the top of the viewport while the left column scrolls, so
 * the balance and the signal state stay on screen next to whatever statement or
 * note row is being read.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Tooltip,
} from "@mui/material";
import Box from "@mui/material/Box";
import ContentCopyOutlined from "@mui/icons-material/ContentCopyOutlined";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import RefreshRounded from "@mui/icons-material/RefreshRounded";
import { useSnackbar } from "notistack";

import Flx from "../../../../../components/layout/Flx";
import Txt from "../../../../../components/typography/Txt";
// Pipeline-aware: under the acquisitions PipelineProvider this resolves to
// /loan_acquisitions/loan_events/{dealId}, which the backend mounts with the
// same implementation as the preclose route.
import { useUnderwritingHook } from "../../../../../hooks/useUnderwritingHook";
import { formatDate } from "../../../../../_src_shared/utils/functions/formatDate";
import { formatDateTime } from "../../../../../_src_shared/utils/functions/formatDateTime";
import formatDollar from "../../../../../_src_shared/utils/functions/formatDollar";
import colors, { status as statusColors } from "../../../../../theme/colors";
import { RISK_STYLE, STATE_STYLE, isLegacyGrading } from "./ocrolusShared";

const MONO =
  'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace';

// "Checked just now" / "Checked 4m ago" / "Checked 2:14 PM".
const checkedLabel = (ts) => {
  if (!ts) return "";
  const mins = Math.floor((Date.now() - ts) / 60000);
  if (mins < 1) return "Checked just now";
  if (mins < 60) return `Checked ${mins}m ago`;
  return `Checked ${formatDateTime(new Date(ts).toISOString(), "time")}`;
};

const StatCell = ({ label, value, sub, valueColor, divider }) => (
  <Flx
    column
    g={0.25}
    sx={{
      flex: 1,
      minWidth: 0,
      px: 2.25,
      py: 1.4,
      ...(divider ? { borderLeft: `1px solid ${colors.slate200}` } : null),
    }}
  >
    <Txt sx={{ fontSize: 11.5, color: colors.slate500 }}>{label}</Txt>
    <Txt sx={{ fontSize: 14, fontWeight: 600, color: valueColor || colors.ink }}>
      {value}
      {sub && (
        <Box
          component="span"
          sx={{ fontSize: 11.5, color: colors.slate500, fontWeight: 500 }}
        >
          {` ${sub}`}
        </Box>
      )}
    </Txt>
  </Flx>
);

const KeyValue = ({ label, children }) => (
  <Flx g={1.5} sx={{ alignItems: "baseline" }}>
    <Txt sx={{ fontSize: 12.5, color: colors.slate500, width: 74, flexShrink: 0 }}>
      {label}
    </Txt>
    <Box sx={{ minWidth: 0, flex: 1 }}>{children}</Box>
  </Flx>
);

const OcrolusBookCard = ({
  book,
  state,
  loading,
  creating,
  dealId,
  onCreateBook,
  onRefresh,
  // Scrolls the left column to the per-document authenticity list.
  onViewSignals,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { getLoanEvents } = useUnderwritingHook();
  const [events, setEvents] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [lastChecked, setLastChecked] = useState(() => Date.now());
  // Re-render on a slow tick so "Checked just now" ages into "4m ago".
  const [, setTick] = useState(0);

  useEffect(() => {
    setLastChecked(Date.now());
  }, [book]);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  // Re-pulled whenever the note stamp moves — a new Ocrolus run rewrites the
  // note and stamps the audit trail in the same pass.
  useEffect(() => {
    if (!dealId) return;
    getLoanEvents({
      dealId,
      onSuccessFn: (res) =>
        setEvents(Array.isArray(res?.events) ? res.events : []),
      onFailFn: () => setEvents([]),
    });
    // getLoanEvents is re-created each render (the hook isn't memoized).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId, book?.ocrolusNote?.updatedAt]);

  const summary = book?.bankStatementSummary;
  const note = book?.ocrolusNote;
  const signals = book?.signals;
  const risk = signals?.riskLevel;
  const riskStyle = RISK_STYLE[risk] || statusColors.neutral;
  const stateStyle = STATE_STYLE[state] || STATE_STYLE.none;
  const isHighRisk = risk === "HIGH" || risk === "MEDIUM";
  // Books graded before 2026-10-15 carry the old signal tally and no
  // per-document list: chip only, plus the regrade hint.
  const legacy = isLegacyGrading(signals);

  // "2 of 4 documents need review" — `documents.length` is the denominator.
  const reviewLine = useMemo(() => {
    if (!signals || legacy) return null;
    const docCount = Array.isArray(signals.documents)
      ? signals.documents.length
      : 0;
    const review = signals.reviewDocuments || 0;
    if (!docCount) return null;
    if (review > 0) {
      return `${review} of ${docCount} document${docCount === 1 ? "" : "s"} need${
        review === 1 && docCount === 1 ? "s" : ""
      } review`;
    }
    return docCount === 1
      ? "The document passed"
      : `All ${docCount} documents passed`;
  }, [signals, legacy]);

  // "3 risk · 5 trust · 2 info" — the three kinds Ocrolus reports, kept apart.
  const countsLine = useMemo(() => {
    if (!signals || legacy) return null;
    return [
      `${signals.total || 0} risk`,
      `${signals.trustSignals || 0} trust`,
      `${signals.infoSignals || 0} info`,
    ].join(" · ");
  }, [signals, legacy]);

  const pipelineLabel = useMemo(() => {
    const p = book?.pipelineCollection;
    if (!p) return null;
    return p === "loan_acquisitions" ? "Acquisitions" : "Pre-Close";
  }, [book?.pipelineCollection]);

  const accounts = useMemo(
    () => (Array.isArray(summary?.accounts) ? summary.accounts : []),
    [summary],
  );

  // The caption under the balance names the account when there's one to name,
  // and falls back to a count when the balance spans several.
  const heroCaption = useMemo(() => {
    if (!accounts.length) return null;
    const asOf = accounts
      .map((a) => a?.latest_end_date)
      .filter(Boolean)
      .sort()
      .pop();
    const when = asOf ? formatDate(asOf, "", "MMM d") : null;
    if (accounts.length === 1) {
      const a = accounts[0];
      return {
        who: [a?.bank_name, a?.last4 ? `····${a.last4}` : null]
          .filter(Boolean)
          .join(" "),
        when,
      };
    }
    return { who: `${accounts.length} accounts`, when };
  }, [accounts]);

  // `cash_flow.net_cash_flow` is credits − debits ACROSS the statements sent —
  // a period total, not a monthly rate. Labelled as such rather than "/mo".
  const netCashFlow = summary?.cash_flow?.net_cash_flow;

  // ── Recent activity ────────────────────────────────────────────────────
  // The real feed: the Ocrolus pipeline stamps `by_topic.ocrolus_summary` and
  // `by_topic.ocrolus_signals` into the structured audit trail, each with its
  // own label, actor and timestamp, and GET /underwriting/loan_events/{dealId}
  // flattens every topic newest-first. That is an event LOG — separate rows per
  // run, real history.
  const auditActivity = useMemo(
    () =>
      (Array.isArray(events) ? events : [])
        .filter((e) => String(e?.event_type || "").startsWith("ocrolus"))
        .filter((e) => e?.timestamp)
        .slice(0, 4)
        .map((e) => ({
          at: e.timestamp,
          what: e.label || e.sub_event || e.event_type,
        })),
    [events],
  );

  // Fallback for loans with no audit document (the stamp no-ops when one
  // doesn't exist yet). These are "last updated" STAMPS, not events: all three
  // are written in the same pipeline pass, so they usually carry the same time
  // and their order between each other means nothing. Real values, weaker
  // claim — which is why the audit feed wins whenever it has anything.
  const derivedActivity = useMemo(() => {
    const items = [];
    if (note?.updatedAt) {
      items.push({
        at: note.updatedAt,
        what: `Analysis note updated to v${note.version}`,
      });
    }
    if (signals?.analyzedAt) {
      items.push({
        at: signals.analyzedAt,
        what: `Authenticity checked · ${risk ? risk.toLowerCase() : "no"} risk`,
      });
    }
    if (summary?.updated_at) {
      items.push({ at: summary.updated_at, what: "Account summary refreshed" });
    }
    // NOTE: book creation isn't in the status payload, so it can't be listed.
    return items
      .filter((i) => i.at)
      .sort((a, b) => String(b.at).localeCompare(String(a.at)))
      .slice(0, 4);
  }, [note, signals, summary, risk]);

  const activity = auditActivity.length ? auditActivity : derivedActivity;

  const handleCopyUuid = useCallback(() => {
    const uuid = book?.book_uuid;
    if (!uuid) return;
    navigator.clipboard?.writeText(uuid);
    enqueueSnackbar("Book ID copied", { variant: "success" });
  }, [book?.book_uuid, enqueueSnackbar]);

  return (
    <Box
      sx={{
        background: colors.white,
        border: `1px solid ${colors.slate200}`,
        borderRadius: "12px",
        overflow: "hidden",
        // Sticky rail: stays with the reader while the left column scrolls.
        // The drilldown's tab bar is itself pinned to the top of the scroller
        // and publishes its measured height as `--drilldown-nav-h` — offset by
        // it or the card slides under the nav. Same pattern as the Documents
        // toolbar and the Loan Info rail.
        position: "sticky",
        top: "calc(var(--drilldown-nav-h, 0px) + 16px)",
        maxHeight: "calc(100vh - var(--drilldown-nav-h, 0px) - 32px)",
        overflowY: "auto",
      }}
    >
      {/* ── Header ─────────────────────────────────────────────────── */}
      <Flx ac g={1} sx={{ px: 2.25, pt: 1.75 }}>
        <Txt
          sx={{ fontSize: 14.5, fontWeight: 600, color: colors.ink, mr: "auto" }}
        >
          Ocrolus book
        </Txt>
        <Txt sx={{ fontSize: 11.5, color: colors.slate400 }}>
          {loading ? "Checking…" : checkedLabel(lastChecked)}
        </Txt>
        <Tooltip title="Refresh status">
          <span>
            <IconButton size="small" onClick={onRefresh} disabled={loading}>
              {loading ? (
                <CircularProgress size={14} />
              ) : (
                <RefreshRounded sx={{ fontSize: 16, color: colors.slate400 }} />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Flx>

      <Flx ac g={1} sx={{ px: 2.25, pt: 1.25 }}>
        <Chip
          size="small"
          label={stateStyle.label}
          sx={{
            height: 23,
            fontSize: 11.5,
            fontWeight: 600,
            bgcolor: stateStyle.bg,
            color: stateStyle.fg,
          }}
        />
        {pipelineLabel && (
          <Chip
            size="small"
            label={pipelineLabel}
            sx={{
              height: 23,
              fontSize: 11.5,
              bgcolor: statusColors.neutral.bg,
              color: statusColors.neutral.fg,
            }}
          />
        )}
      </Flx>

      {/* ── No book ────────────────────────────────────────────────── */}
      {state === "none" && (
        <Flx column ac g={0.5} sx={{ px: 2.5, pt: 3, pb: 3, textAlign: "center" }}>
          <DescriptionOutlined sx={{ fontSize: 30, color: colors.slate300 }} />
          <Txt sx={{ fontSize: 14, fontWeight: 600, color: colors.ink, mt: 1 }}>
            No Ocrolus book yet
          </Txt>
          <Txt
            sx={{ fontSize: 12.5, color: colors.slate500, mb: 1.5 }}
            component="div"
          >
            Select statements on the left and send them — a book is created
            automatically. One book per loan.
          </Txt>
          <Button
            size="small"
            onClick={onCreateBook}
            disabled={creating || !dealId}
          >
            {creating ? (
              <CircularProgress size={16} sx={{ color: "inherit" }} />
            ) : (
              "Create book now"
            )}
          </Button>
        </Flx>
      )}

      {/* ── Processing: results arrive by webhook, usually minutes ──── */}
      {state === "processing" && (
        <Flx column ac g={0.5} sx={{ px: 2.5, pt: 3, pb: 2.5, textAlign: "center" }}>
          <CircularProgress size={22} sx={{ color: statusColors.info.fg }} />
          <Txt sx={{ fontSize: 14, fontWeight: 600, color: colors.ink, mt: 1.5 }}>
            {`Analyzing ${book?.filesSent || 0} statement${
              book?.filesSent === 1 ? "" : "s"
            }`}
          </Txt>
          <Txt sx={{ fontSize: 12.5, color: colors.slate500 }} component="div">
            Usually takes a few minutes. Refresh to pick up the results.
          </Txt>
        </Flx>
      )}

      {/* ── Book exists, nothing sent ──────────────────────────────── */}
      {state === "empty" && (
        <Flx column ac g={0.5} sx={{ px: 2.5, pt: 3, pb: 2.5, textAlign: "center" }}>
          <Txt sx={{ fontSize: 14, fontWeight: 600, color: colors.ink }}>
            No statements sent yet
          </Txt>
          <Txt sx={{ fontSize: 12.5, color: colors.slate500 }} component="div">
            Select bank statements on the left and send them to this book.
          </Txt>
        </Flx>
      )}

      {/* ── Ready: the analysis ────────────────────────────────────── */}
      {state === "ready" && (
        <>
          <Box sx={{ px: 2.25, pt: 2, pb: 1.75 }}>
            <Txt
              sx={{
                fontSize: 27,
                fontWeight: 700,
                letterSpacing: "-0.02em",
                fontFamily: MONO,
                lineHeight: 1.1,
                color: colors.ink,
              }}
            >
              {summary?.total_latest_balance == null
                ? "—"
                : formatDollar(summary.total_latest_balance, "—")}
            </Txt>
            {heroCaption && (
              <Txt
                sx={{ fontSize: 12.5, color: colors.slate400, mt: 0.6 }}
                component="div"
              >
                Verified balance ·{" "}
                <Box
                  component="span"
                  sx={{ color: colors.slate600, fontWeight: 500 }}
                >
                  {heroCaption.who}
                </Box>
                {heroCaption.when ? ` · as of ${heroCaption.when}` : ""}
              </Txt>
            )}
          </Box>

          <Flx
            sx={{
              borderTop: `1px solid ${colors.slate200}`,
              borderBottom: `1px solid ${colors.slate200}`,
            }}
          >
            <StatCell
              label="Net cash flow"
              value={netCashFlow == null ? "—" : formatDollar(netCashFlow, "—")}
              sub="across statements"
              valueColor={
                netCashFlow > 0 ? statusColors.success.fg : colors.ink
              }
            />
            <StatCell
              divider
              label="Analyzed"
              value={`${book?.filesSent || 0} file${
                book?.filesSent === 1 ? "" : "s"
              }`}
              sub={`· ${summary?.account_count ?? 0} account${
                summary?.account_count === 1 ? "" : "s"
              }`}
            />
          </Flx>

          {/* Authenticity verdict, graded on Ocrolus's own scale (backend
              2026-10-15): colour from the worst document, text from how many
              documents need review. Never a bare "N signals" count — the old
              tally mixed Ocrolus's trust signals in with its risk signals.
              Clicking jumps to the per-document list on the left. */}
          {risk && (
            <Box
              component={onViewSignals ? "button" : "div"}
              onClick={onViewSignals}
              sx={{
                display: "block",
                width: "calc(100% - 36px)",
                textAlign: "left",
                m: "14px 18px 0",
                p: 1.25,
                borderRadius: "9px",
                border: `1px solid ${riskStyle.fg}33`,
                background: riskStyle.bg,
                cursor: onViewSignals ? "pointer" : "default",
                font: "inherit",
              }}
            >
              <Flx ac g={1.25}>
                <Txt sx={{ fontSize: 12.5, fontWeight: 600, color: riskStyle.fg }}>
                  Authenticity
                </Txt>
                <Box
                  component="span"
                  sx={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    letterSpacing: "0.05em",
                    borderRadius: "5px",
                    px: "7px",
                    py: "2px",
                    color: colors.white,
                    background: riskStyle.solid,
                  }}
                >
                  {`${risk} RISK`}
                </Box>
                {signals.analyzedAt && (
                  <Txt
                    sx={{
                      fontSize: 11.5,
                      color: colors.slate500,
                      ml: "auto",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formatDateTime(signals.analyzedAt, "time")}
                  </Txt>
                )}
              </Flx>
              {reviewLine && (
                <Txt
                  sx={{ fontSize: 13, fontWeight: 600, color: colors.ink, mt: 0.75 }}
                  component="div"
                >
                  {reviewLine}
                </Txt>
              )}
              {countsLine && (
                <Txt
                  sx={{ fontSize: 11.5, color: colors.slate500, mt: 0.25 }}
                  component="div"
                >
                  {countsLine}
                </Txt>
              )}
              {legacy && (
                <Txt
                  sx={{ fontSize: 12, color: colors.slate600, mt: 0.75, lineHeight: 1.5 }}
                  component="div"
                >
                  Graded before Ocrolus's per-document scores were used. Re-sync
                  with Ocrolus to regrade.
                </Txt>
              )}
              {isHighRisk && (
                <Txt
                  sx={{ fontSize: 12, color: riskStyle.fg, mt: 0.75, lineHeight: 1.5 }}
                  component="div"
                >
                  A review condition and an inbox notification were created
                  automatically.
                  {legacy ? "" : " Each document's result is listed on the left."}
                </Txt>
              )}
            </Box>
          )}

          {activity.length > 0 && (
            <Box sx={{ px: 2.25, pt: 1.75, pb: 0.5 }}>
              <Txt
                sx={{ fontSize: 11.5, color: colors.slate500, mb: 1 }}
                component="div"
              >
                Recent activity
              </Txt>
              {activity.map((a, i) => (
                <Flx key={a.at} g={1.25} sx={{ alignItems: "baseline", py: 0.5 }}>
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      flexShrink: 0,
                      position: "relative",
                      top: -1,
                      background: i === 0 ? colors.action : colors.slate300,
                    }}
                  />
                  <Txt sx={{ fontSize: 12.5, color: colors.slate600 }}>
                    {a.what}
                  </Txt>
                  <Txt
                    sx={{
                      fontSize: 11.5,
                      color: colors.slate400,
                      ml: "auto",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formatDateTime(a.at, "time")}
                  </Txt>
                </Flx>
              ))}
            </Box>
          )}
        </>
      )}

      {/* ── Book details (reference, folded away) ──────────────────── */}
      {state !== "none" && (
        <Box sx={{ borderTop: `1px solid ${colors.slate200}`, mt: 1.5 }}>
          <Flx
            ac
            g={1}
            onClick={() => setDetailsOpen((o) => !o)}
            sx={{
              px: 2.25,
              py: 1.4,
              cursor: "pointer",
              "&:hover": { background: colors.slate50 },
            }}
          >
            <Txt sx={{ fontSize: 12.5, fontWeight: 600, color: colors.slate600 }}>
              Book details
            </Txt>
            <ExpandMoreRounded
              sx={{
                ml: "auto",
                fontSize: 18,
                color: colors.slate400,
                transition: "transform .15s",
                transform: detailsOpen ? "rotate(180deg)" : "none",
              }}
            />
          </Flx>
          <Collapse in={detailsOpen}>
            <Flx column g={1} sx={{ px: 2.25, pb: 2 }}>
              <KeyValue label="Name">
                <Txt sx={{ fontSize: 12.5, fontWeight: 500, color: colors.ink }}>
                  {book?.book_name || summary?.book_name || "—"}
                </Txt>
              </KeyValue>
              <KeyValue label="Book ID">
                <Flx ac g={0.5} sx={{ minWidth: 0 }}>
                  <Txt
                    sx={{
                      fontSize: 11,
                      fontFamily: MONO,
                      color: colors.slate600,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {book?.book_uuid || "—"}
                  </Txt>
                  {book?.book_uuid && (
                    <Tooltip title="Copy full book ID">
                      <IconButton size="small" onClick={handleCopyUuid}>
                        <ContentCopyOutlined
                          sx={{ fontSize: 13, color: colors.slate400 }}
                        />
                      </IconButton>
                    </Tooltip>
                  )}
                </Flx>
              </KeyValue>
              {pipelineLabel && (
                <KeyValue label="Pipeline">
                  <Txt sx={{ fontSize: 12.5, fontWeight: 500, color: colors.ink }}>
                    {pipelineLabel}
                  </Txt>
                </KeyValue>
              )}
              {note?.updatedAt && (
                <KeyValue label="Note">
                  <Txt sx={{ fontSize: 12.5, fontWeight: 500, color: colors.ink }}>
                    {`v${note.version} · ${formatDateTime(note.updatedAt)}`}
                  </Txt>
                </KeyValue>
              )}
            </Flx>
          </Collapse>
        </Box>
      )}

      {/* ── Footer action ──────────────────────────────────────────── */}
      {state !== "none" && (
        <Box
          sx={{ borderTop: `1px solid ${colors.slate200}`, px: 2.25, py: 1.5 }}
        >
          <Button
            fullWidth
            variant="outlined"
            size="small"
            onClick={onCreateBook}
            disabled={creating || !dealId}
          >
            {creating ? (
              <CircularProgress size={16} />
            ) : (
              "Re-sync with Ocrolus"
            )}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default OcrolusBookCard;
